import { useState, useEffect } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { ChildrenProps } from '@gv/triage-components';

import { Config } from 'config';

let stripePromise: Promise<Stripe | null> | undefined = undefined;

const isChromeExtension =
  typeof window !== 'undefined' &&
  typeof (window as any).chrome !== 'undefined' &&
  (window as any).chrome?.runtime?.id;

const getStripePromise = async (): Promise<Stripe | null> => {
  if (isChromeExtension) {
    return null;
  }

  if (!stripePromise) {
    try {
      const { loadStripe } = await import('@stripe/stripe-js');
      stripePromise = loadStripe(Config.stripeKey!);
    } catch (error) {
      console.warn('Stripe initialization failed:', error);
      return null;
    }
  }
  return stripePromise || null;
};

export const StripeElements = ({ children }: ChildrenProps) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (isChromeExtension) {
      setStripe(null);
      setIsLoading(false);
      setHasError(true);
      return;
    }

    getStripePromise()
      .then((stripeInstance) => {
        setStripe(stripeInstance);
        setIsLoading(false);
        if (!stripeInstance) {
          setHasError(true);
        }
      })
      .catch((error) => {
        console.warn('Stripe loading failed:', error);
        setStripe(null);
        setIsLoading(false);
        setHasError(true);
      });
  }, []);

  if (isLoading) {
    return <>{children}</>;
  }

  if (hasError || !stripe) {
    return (
      <Elements stripe={null} options={{ locale: 'en' }}>
        {children}
      </Elements>
    );
  }

  return (
    <Elements stripe={stripe} options={{ locale: 'en' }}>
      {children}
    </Elements>
  );
};
